import React, { useState } from 'react';
import { AppBar, Toolbar, IconButton, Menu, MenuItem, Typography, Box, Avatar, useTheme, useMediaQuery, Button, Tooltip } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getSiteName } from '../config/appConfig';
import ChangePasswordDialog from './ChangePasswordDialog';

interface HeaderProps {
  onMenuClick?: () => void;
  desktopOpen?: boolean;
  onDesktopToggle?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, desktopOpen = true, onDesktopToggle }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const apkDownloadUrl = process.env.REACT_APP_ANDROID_APK_URL || '/assets/eone-app.apk';

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    handleClose();
    setChangePasswordOpen(true);
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        color: '#000000',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)'
      }}
    >
      <Toolbar>
        {isMobile && (
          <Tooltip title="Abrir menú">
          <IconButton
            color="inherit"
            aria-label="abrir menú"
            edge="start"
            onClick={onMenuClick}
              sx={{ 
                mr: 2, 
                color: '#00548f',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                }
              }}
          >
            <MenuIcon />
          </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Box
            component={Link}
            to="/dashboard"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <Box
              component="img"
              src="/assets/eone-logo.png"
              alt="eOne"
              sx={{
                height: { xs: 52, sm: 56, md: 64 },
                width: 'auto'
              }}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.02em',
              fontFamily: [
                '-apple-system',
                'BlinkMacSystemFont',
                '"Segoe UI"',
                'Roboto',
                '"Helvetica Neue"',
                'Arial',
                'sans-serif',
              ].join(','),
              display: { xs: 'none', sm: 'block' },
              mr: 2,
              fontSize: '1rem'
            }}
          >
            {getSiteName()}
          </Typography>
          <IconButton
            onClick={handleMenu}
            sx={{ 
              padding: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)'
              }
            }}
          >
            <Avatar 
              sx={{ 
                bgcolor: '#1976d2',
                color: 'white',
                width: 35,
                height: 35
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>
              <Typography>{user?.username}</Typography>
            </MenuItem>
            <MenuItem
              component="a"
              href={apkDownloadUrl}
              download
              onClick={handleClose}
            >
              <Typography>Descargar App Vendedores</Typography>
            </MenuItem>
            <MenuItem onClick={handleChangePassword}>
              <Typography>Cambiar Contraseña</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Typography>Cerrar Sesión</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </AppBar>
  );
};

export default Header; 